/**
 * Theme Builder
 * Drag-and-drop visual editor for storefront themes using Craft.js
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor, Frame, Element, useEditor } from '@craftjs/core';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import './ThemeBuilder.css';

// Editable Section Components
import {
  EditorHeroBanner,
  EditorHeroSlider,
  EditorFeaturedProducts,
  EditorAllProducts,
  EditorCategoriesGrid,
  EditorTestimonials,
  EditorNewsletter,
  EditorTextImage,
  EditorVideoBanner,
  EditorTrustBadges,
  EditorAnnouncementBar,
  EditorAdPlacement,
} from './EditorSections';

// Container component for the canvas
import { EditorCanvas } from './EditorCanvas';

// Settings Panel
import SettingsPanel from './SettingsPanel';
import { EditionSection } from '../../themes/edition/EditionHomePage';
import { AtelierSection } from '../../themes/atelier/AtelierHomePage';
import { GallerySection } from '../../themes/gallery/GalleryHomePage';
import { EDITION_SECTION_META, createEditionSections, isEditionSection } from '../../themes/edition/editionSections';
import { ATELIER_SECTION_META, createAtelierSections, isAtelierSection } from '../../themes/atelier/atelierSections';
import { GALLERY_SECTION_META, createGallerySections, isGallerySection } from '../../themes/gallery/gallerySections';
import { COMMON_THEME_SECTION_META, isCommonThemeSection, ThemeExtension } from '../../themes/ThemeExtensions';
import ThemeStyleProvider from '../../components/theme-sections/ThemeStyleProvider';

// Section type to component mapping
const sectionComponents = {
  'hero-banner': EditorHeroBanner,
  'hero-slider': EditorHeroSlider,
  'featured-products': EditorFeaturedProducts,
  'all-products': EditorAllProducts,
  'categories-grid': EditorCategoriesGrid,
  'testimonials': EditorTestimonials,
  'newsletter': EditorNewsletter,
  'text-image': EditorTextImage,
  'video-banner': EditorVideoBanner,
  'trust-badges': EditorTrustBadges,
  'announcement-bar': EditorAnnouncementBar,
  'ad-placement': EditorAdPlacement,
};

// Base section metadata for the picker (always available)
const baseSectionMeta = [
  { type: 'hero-banner', name: 'Hero Banner', icon: 'layout', description: 'Full-width hero with CTA' },
  { type: 'hero-slider', name: 'Hero Slider', icon: 'images', description: 'Carousel of hero images' },
  { type: 'video-banner', name: 'Video Banner', icon: 'play', description: 'YouTube, Vimeo, or self-hosted video' },
  { type: 'featured-products', name: 'Featured Products', icon: 'grid', description: 'Product grid display' },
  { type: 'all-products', name: 'All Products', icon: 'grid', description: 'Full catalog with search and filters' },
  { type: 'categories-grid', name: 'Categories', icon: 'folder', description: 'Category cards grid' },
  { type: 'testimonials', name: 'Testimonials', icon: 'quote', description: 'Customer reviews' },
  { type: 'newsletter', name: 'Newsletter', icon: 'mail', description: 'Email signup form' },
  { type: 'text-image', name: 'Text + Image', icon: 'columns', description: 'Split content block' },
  { type: 'trust-badges', name: 'Trust Badges', icon: 'shield', description: 'Trust indicators' },
  { type: 'announcement-bar', name: 'Announcement', icon: 'megaphone', description: 'Top banner' },
];

// Premium section - Ad Placement (only shown if merchant has ad feature access)
const adSectionMeta = { type: 'ad-placement', name: 'Ad Placement', icon: 'ad', description: 'Display storefront ads', premium: true };

function ThemeBuilder() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);
  const [themeConfig, setThemeConfig] = useState(null);
  const [theme, setTheme] = useState(null);
  const [globalStyles, setGlobalStyles] = useState({});
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [devicePreview, setDevicePreview] = useState('desktop');
  const [appearancePreview, setAppearancePreview] = useState('light');
  const [hasChanges, setHasChanges] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [showShopSettings, setShowShopSettings] = useState(false);
  const [hasAdFeatureAccess, setHasAdFeatureAccess] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);

  // Build section metadata - include ad-placement only if merchant has access
  const isEditionTheme = theme?.slug === 'edition';
  const isAtelierTheme = theme?.slug === 'atelier';
  const isGalleryTheme = theme?.slug === 'gallery';
  const sectionMeta = isEditionTheme
    ? [...EDITION_SECTION_META, ...COMMON_THEME_SECTION_META]
    : isAtelierTheme
      ? [...ATELIER_SECTION_META, ...COMMON_THEME_SECTION_META]
      : isGalleryTheme
        ? [...GALLERY_SECTION_META, ...COMMON_THEME_SECTION_META]
      : (hasAdFeatureAccess ? [...baseSectionMeta, adSectionMeta] : baseSectionMeta);

  // Load theme config and check ad feature access on mount
  useEffect(() => {
    loadThemeConfig();
    checkAdFeatureAccess();
  }, []);

  // Check if merchant has access to ad feature
  const checkAdFeatureAccess = async () => {
    try {
      const response = await axiosInstance.get('/merchant/ads/status');
      if (response.data?.access?.hasAccess) {
        setHasAdFeatureAccess(true);
      }
    } catch (error) {
      // If error or no access, keep hasAdFeatureAccess as false
      console.log('Ad feature access check:', error.response?.status === 403 ? 'No access' : 'Error');
    }
  };

  const loadThemeConfig = async () => {
    try {
      setLoading(true);

      // Get merchant's store and theme config
      const res = await axiosInstance.get('/themes/merchant/theme-config');

      if (res.data.success) {
        const { store, themeConfig, theme } = res.data;
          setStore(store);
        setThemeConfig(themeConfig);
        setTheme(theme);

        if (theme) {
          // Merge theme defaults with any overrides
          const mergedStyles = {
            ...theme.globalSettings,
            ...(themeConfig?.globalOverrides || {}),
          };
          setGlobalStyles(mergedStyles);

          // Get sections from page config or theme defaults
          const homePageConfig = themeConfig?.pages?.find(p => p.pageType === 'HOME');
          const savedSections = homePageConfig?.sections?.length > 0
            ? homePageConfig.sections
            : theme.sections || [];
          const initialSections = theme.slug === 'edition'
            ? (savedSections.filter(isEditionSection).length ? savedSections.filter(isEditionSection) : createEditionSections())
            : theme.slug === 'atelier'
              ? (savedSections.filter(isAtelierSection).length ? savedSections.filter(isAtelierSection) : createAtelierSections())
              : theme.slug === 'gallery'
                ? (savedSections.filter(isGallerySection).length ? savedSections.filter(isGallerySection) : createGallerySections())
                : savedSections;
          setSections(initialSections);

          // Edition's curated modules use the merchant's real catalogue. A
          // failed catalogue request must not prevent the rest of the editor
          // from loading; the live renderer has safe automatic fallbacks.
          try {
            const catalogueRes = await axiosInstance.get('/products/public', {
              params: { storeSlug: store.slug, limit: 100 },
            });
            setCatalogProducts(catalogueRes.data?.products || []);
          } catch (catalogueError) {
            console.warn('Could not load catalogue for theme editor:', catalogueError);
            setCatalogProducts([]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load theme config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);

      await axiosInstance.put('/themes/merchant/theme-config', {
        globalOverrides: globalStyles,
        pages: [
          {
            pageType: 'HOME',
            sections: sections,
          },
        ],
      });

      setHasChanges(false);
      showToast('Changes saved', 'success');
    } catch (err) {
      console.error('Failed to save draft:', err);
      showToast('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);

      await axiosInstance.post('/themes/merchant/theme-config/publish', {
        globalOverrides: globalStyles,
        pages: [
          {
            pageType: 'HOME',
            sections: sections,
          },
        ],
      });

      setHasChanges(false);
      showToast('Theme published successfully!', 'success');
    } catch (err) {
      console.error('Failed to publish:', err);
      showToast('Failed to publish theme', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = (sectionType) => {
    const newSection = {
      id: `${sectionType}-${Date.now()}`,
      type: sectionType,
      settings: getDefaultSettings(sectionType),
      content: getDefaultContent(sectionType),
    };

    setSections(prev => [...prev, newSection]);
    setSelectedSectionId(newSection.id);
    setHasChanges(true);
  };

  const handleRemoveSection = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
    if (selectedSectionId === sectionId) {
      setSelectedSectionId(null);
    }
    setHasChanges(true);
  };

  const handleMoveSection = (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
    setHasChanges(true);
  };

  const handleReorderSection = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;

    setSections((previousSections) => {
      const sourceIndex = previousSections.findIndex((section) => section.id === sourceId);
      const targetIndex = previousSections.findIndex((section) => section.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return previousSections;

      const nextSections = [...previousSections];
      const [movedSection] = nextSections.splice(sourceIndex, 1);
      nextSections.splice(sourceIndex < targetIndex ? targetIndex - 1 : targetIndex, 0, movedSection);
      return nextSections;
    });
    setHasChanges(true);
  };

  const handleDuplicateSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    const index = sections.findIndex(s => s.id === sectionId);
    const newSection = {
      ...section,
      id: `${section.type}-${Date.now()}`,
    };

    const newSections = [...sections];
    newSections.splice(index + 1, 0, newSection);
    setSections(newSections);
    setSelectedSectionId(newSection.id);
    setHasChanges(true);
  };

  const handleUpdateSection = (sectionId, updates) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, ...updates } : s
    ));
    setHasChanges(true);
  };

  const handleUpdateGlobalStyles = (updates) => {
    setGlobalStyles(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const selectedSection = sections.find(s => s.id === selectedSectionId);

  if (loading) {
    return (
      <div className="tb-loading">
        <div className="tb-loading__spinner" />
        <p>Loading theme builder...</p>
      </div>
    );
  }

  if (!theme) {
    return (
      <div className="tb-no-theme">
        <div className="tb-no-theme__content">
          <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
          <h2>No Theme Selected</h2>
          <p>You need to activate a theme before you can customize it.</p>
          <button
            className="tb-no-theme__btn"
            onClick={() => navigate('/dashboard/themes')}
          >
            Browse Themes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-builder">
      {/* Top Bar */}
      <header className="tb-header">
        <div className="tb-header__left">
          <button className="tb-header__back" onClick={() => navigate(-1)}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="tb-header__info">
            <h1 className="tb-header__title">Theme Builder</h1>
            <span className="tb-header__theme-name">{theme.name}</span>
          </div>
        </div>

        <div className="tb-header__center">
          <div className="tb-device-toggle">
            <button
              className={`tb-device-btn ${devicePreview === 'mobile' ? 'active' : ''}`}
              onClick={() => setDevicePreview('mobile')}
              title="Mobile"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12" y2="18" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              className={`tb-device-btn ${devicePreview === 'tablet' ? 'active' : ''}`}
              onClick={() => setDevicePreview('tablet')}
              title="Tablet"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <line x1="12" y1="18" x2="12" y2="18" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              className={`tb-device-btn ${devicePreview === 'desktop' ? 'active' : ''}`}
              onClick={() => setDevicePreview('desktop')}
              title="Desktop"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </button>
          </div>
          <span className="tb-preview-label">{devicePreview === 'desktop' ? 'Desktop · fluid' : devicePreview === 'tablet' ? 'Tablet · 768px' : 'Mobile · 390px'}</span>
          <div className="tb-appearance-toggle" aria-label="Preview appearance">
            <button type="button" aria-pressed={appearancePreview === 'light'} className={appearancePreview === 'light' ? 'active' : ''} onClick={() => setAppearancePreview('light')}>Light</button>
            <button type="button" aria-pressed={appearancePreview === 'dark'} className={appearancePreview === 'dark' ? 'active' : ''} onClick={() => setAppearancePreview('dark')}>Dark</button>
          </div>
        </div>

        <div className="tb-header__right">
          {hasChanges && <span className="tb-header__unsaved">Unsaved changes</span>}
          <button className="tb-header__btn tb-header__btn--secondary tb-header__palette" onClick={() => setShowGlobalSettings(true)}>
            Palette
          </button>
          <button
            className="tb-header__btn tb-header__btn--secondary"
            onClick={handleSaveDraft}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            className="tb-header__btn tb-header__btn--primary"
            onClick={handlePublish}
            disabled={saving}
          >
            Publish
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="tb-main">
        {/* Left Panel - Section Picker */}
        <aside className="tb-sidebar tb-sidebar--left">
          {/* Settings buttons at top */}
          <div className="tb-sidebar__settings">
            <button
              className="tb-global-settings-btn"
              onClick={() => setShowGlobalSettings(true)}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              Global
            </button>
            <button
              className="tb-global-settings-btn"
              onClick={() => setShowShopSettings(true)}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Shop
            </button>
          </div>

          <div className="tb-sidebar__header">
            <h3>Add Sections</h3>
          </div>

          <div className="tb-section-picker">
            {Array.from({ length: Math.ceil(sectionMeta.length / 2) }, (_, rowIndex) => (
              <div className="tb-section-picker__row" key={`section-row-${rowIndex}`}>
                {sectionMeta.slice(rowIndex * 2, rowIndex * 2 + 2).map((section) => (
                  <button
                    key={section.type}
                    className="tb-section-picker__item"
                    onClick={() => handleAddSection(section.type)}
                    title={section.description}
                  >
                    <div className="tb-section-picker__icon">
                      <SectionIcon type={section.icon} />
                    </div>
                    <div className="tb-section-picker__info">
                      <span className="tb-section-picker__name">{section.name}</span>
                    </div>
                  </button>
                ))}
                {sectionMeta.slice(rowIndex * 2, rowIndex * 2 + 2).length === 1 && <span className="tb-section-picker__empty-slot" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </aside>

        {/* Center - Preview Canvas */}
        <main className={`tb-canvas tb-canvas--${devicePreview}`}>
          <div className="tb-canvas__wrapper">
            <div className="tb-storefront-preview" data-storefront-theme={appearancePreview}>
            <ThemeStyleProvider styles={globalStyles}>
            {sections.length === 0 ? (
              <div className="tb-canvas__empty">
                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <p>Add sections from the left panel to start building</p>
              </div>
            ) : (
              <div className="tb-canvas__sections">
                {sections.map((section, index) => (
                  <SectionWrapper
                    key={section.id}
                    section={section}
                    index={index}
                    isSelected={selectedSectionId === section.id}
                    onSelect={() => setSelectedSectionId(section.id)}
                    onRemove={() => handleRemoveSection(section.id)}
                    onMoveUp={() => handleMoveSection(section.id, 'up')}
                    onMoveDown={() => handleMoveSection(section.id, 'down')}
                    onDuplicate={() => handleDuplicateSection(section.id)}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', section.id);
                      setDraggedSectionId(section.id);
                    }}
                    onDragEnd={() => setDraggedSectionId(null)}
                    onDrop={() => {
                      handleReorderSection(draggedSectionId, section.id);
                      setDraggedSectionId(null);
                    }}
                    isDragging={draggedSectionId === section.id}
                    canMoveUp={index > 0}
                    canMoveDown={index < sections.length - 1}
                    globalStyles={globalStyles}
                    store={store}
                    industry={theme?.industry}
                    products={catalogProducts}
                  />
                ))}
              </div>
            )}
            </ThemeStyleProvider>
            </div>
          </div>
        </main>

        {/* Right Panel - Settings */}
        <aside className="tb-sidebar tb-sidebar--right">
          {selectedSection ? (
            <SettingsPanel
              section={selectedSection}
              onUpdate={(updates) => handleUpdateSection(selectedSectionId, updates)}
              onClose={() => setSelectedSectionId(null)}
              globalStyles={globalStyles}
              availableProducts={catalogProducts}
            />
          ) : (
            <div className="tb-sidebar__empty">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <p>Select a section to edit its settings</p>
            </div>
          )}
        </aside>
      </div>

      {/* Global Settings Modal */}
      {showGlobalSettings && (
        <GlobalSettingsModal
          styles={globalStyles}
          onUpdate={handleUpdateGlobalStyles}
          onClose={() => setShowGlobalSettings(false)}
          onOpenStorefrontSettings={() => navigate('/manage-store/storefront-settings')}
        />
      )}

      {/* Shop Settings Modal */}
      {showShopSettings && (
        <ShopSettingsModal
          settings={globalStyles.shopLayout || {}}
          onUpdate={(shopLayout) => handleUpdateGlobalStyles({ shopLayout })}
          onClose={() => setShowShopSettings(false)}
        />
      )}
    </div>
  );
}

// Section Wrapper with controls
function SectionWrapper({
  section,
  index,
  isSelected,
  onSelect,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDragStart,
  onDragEnd,
  onDrop,
  isDragging,
  canMoveUp,
  canMoveDown,
  globalStyles,
  store,
  industry,
  products,
}) {
  const SectionPreview = getSectionPreview(section);

  return (
    <div
      className={`tb-section-wrapper ${isSelected ? 'tb-section-wrapper--selected' : ''} ${isDragging ? 'tb-section-wrapper--dragging' : ''}`}
      onClick={onSelect}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => { event.preventDefault(); onDrop(); }}
    >
      {/* Section Controls */}
      <div className="tb-section-controls">
        <div className="tb-section-controls__identity">
          <span className="tb-section-controls__handle" title="Drag to reorder" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="6" r="1"/><circle cx="16" cy="6" r="1"/><circle cx="8" cy="12" r="1"/><circle cx="16" cy="12" r="1"/><circle cx="8" cy="18" r="1"/><circle cx="16" cy="18" r="1"/></svg>
          </span>
          <span className="tb-section-controls__index">{String(index + 1).padStart(2, '0')}</span>
          <span className="tb-section-controls__label">{getSectionName(section.type)}</span>
        </div>
        <div className="tb-section-controls__actions">
          <button
            className="tb-section-controls__btn"
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={!canMoveUp}
            title="Move up"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
          <button
            className="tb-section-controls__btn"
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={!canMoveDown}
            title="Move down"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <button
            className="tb-section-controls__btn"
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            title="Duplicate"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
          </button>
          <button
            className="tb-section-controls__btn tb-section-controls__btn--danger"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Remove"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Section Preview */}
      <div className="tb-section-preview">
        <SectionPreview
          settings={section.settings || {}}
          content={section.content || {}}
          globalStyles={globalStyles}
          store={store}
          industry={industry}
          products={products}
        />
      </div>
    </div>
  );
}

// Get preview component for a section type
function getSectionPreview(section) {
  if (isEditionSection(section)) {
    return (props) => <div className="edition-home"><EditionSection section={{ ...section, ...props }} store={props.store} products={props.products} formatPrice={(value) => previewPrice(value, props.store?.currency)} /></div>;
  }
  if (isAtelierSection(section)) {
    return (props) => <div className="atelier-home"><AtelierSection section={{ ...section, ...props }} store={props.store} products={props.products} formatPrice={(value) => previewPrice(value, props.store?.currency)} /></div>;
  }
  if (isGallerySection(section)) {
    return (props) => <div className="gallery-home"><GallerySection section={{ ...section, ...props }} store={props.store} products={props.products} formatPrice={(value) => previewPrice(value, props.store?.currency)} /></div>;
  }
  if (isCommonThemeSection(section)) {
    return (props) => <ThemeExtension section={{ ...section, ...props }} preview />;
  }
  // Import the actual section components for preview
  const previews = {
    'hero-banner': require('../../components/theme-sections/HeroBanner').default,
    'hero-slider': require('../../components/theme-sections/HeroSlider').default,
    'featured-products': require('../../components/theme-sections/FeaturedProducts').default,
    'all-products': require('../../components/theme-sections/AllProducts').default,
    'categories-grid': require('../../components/theme-sections/CategoriesGrid').default,
    'testimonials': require('../../components/theme-sections/Testimonials').default,
    'newsletter': require('../../components/theme-sections/Newsletter').default,
    'text-image': require('../../components/theme-sections/TextImage').default,
    'video-banner': require('../../components/theme-sections/VideoBanner').default,
    'trust-badges': require('../../components/theme-sections/TrustBadges').default,
    'announcement-bar': require('../../components/theme-sections/AnnouncementBar').default,
    'ad-placement': require('../../components/theme-sections/AdSection').default,
  };

  return previews[section.type] || (() => <div className="tb-unknown-section">Unknown section: {section.type}</div>);
}

function previewPrice(value, currency = 'NGN') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency, maximumFractionDigits: 2 });
  } catch {
    return amount.toLocaleString();
  }
}

// Complete section metadata for name lookup (includes all sections)
const allSectionMeta = [...baseSectionMeta, adSectionMeta, ...EDITION_SECTION_META, ...ATELIER_SECTION_META, ...GALLERY_SECTION_META, ...COMMON_THEME_SECTION_META];

// Get section name from type
function getSectionName(type) {
  const meta = allSectionMeta.find(m => m.type === type);
  return meta?.name || type;
}

// Get default settings for a section type
function getDefaultSettings(type) {
  const collectionDefault = [...createEditionSections(), ...createAtelierSections(), ...createGallerySections()].find((section) => section.type === type);
  if (collectionDefault) return collectionDefault.settings;
  if (type === 'theme-promo' || type === 'theme-faq') return {};
  const defaults = {
    'hero-banner': { height: '500px', overlayOpacity: 0.3, textAlign: 'center' },
    'hero-slider': { height: '500px', autoplay: true, interval: 5000 },
    'video-banner': { height: '500px', autoplay: true, muted: true, loop: true, showControls: false, overlayOpacity: 0.3, objectFit: 'cover' },
    'featured-products': { count: 8, columns: 4, showPrice: true },
    'all-products': { productsPerPage: 12, defaultView: 'grid', showControls: true },
    'categories-grid': { columns: 4, showCount: true, imageStyle: 'square' },
    'testimonials': { layout: 'carousel', showPhotos: true, maxItems: 6 },
    'newsletter': { layout: 'horizontal', backgroundColor: '#f9fafb' },
    'text-image': { imagePosition: 'right', backgroundColor: '#ffffff' },
    'trust-badges': { layout: 'horizontal', backgroundColor: '#ffffff' },
    'announcement-bar': { backgroundColor: '#1a1a2e', textColor: '#ffffff' },
    'ad-placement': { placement: 'HOMEPAGE_BANNER', showAdvertiseBlurb: true, maxAds: 1, backgroundColor: 'transparent', padding: '20px 0' },
  };
  return defaults[type] || {};
}

// Get default content for a section type
function getDefaultContent(type) {
  const collectionDefault = [...createEditionSections(), ...createAtelierSections(), ...createGallerySections()].find((section) => section.type === type);
  if (collectionDefault) return collectionDefault.content;
  if (type === 'theme-promo') return { eyebrow: 'Just in', title: 'A little something worth seeing.', body: '', buttonText: 'Explore now', buttonLink: '/shop' };
  if (type === 'theme-faq') return { eyebrow: 'Need to know', title: 'A few helpful answers.' };
  const defaults = {
    'hero-banner': {
      title: 'Welcome to Our Store',
      subtitle: 'Discover amazing products',
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      backgroundImage: '',
    },
    'hero-slider': {
      slides: [
        { title: 'Slide 1', subtitle: 'Description', buttonText: 'Shop', buttonLink: '/shop' },
      ],
    },
    'video-banner': {
      videoUrl: '',
      posterImage: '',
      title: 'Welcome',
      subtitle: 'Explore our collection',
      buttonText: 'Shop Now',
      buttonLink: '/shop',
    },
    'featured-products': {
      title: 'Featured Products',
      subtitle: 'Our best sellers',
    },
    'all-products': {
      title: 'All Products',
      subtitle: 'Browse everything in store',
    },
    'categories-grid': {
      title: 'Shop by Category',
      subtitle: '',
    },
    'testimonials': {
      title: 'What Customers Say',
      testimonials: [],
    },
    'newsletter': {
      title: 'Subscribe to our Newsletter',
      description: 'Get updates on new products and offers',
      buttonText: 'Subscribe',
    },
    'text-image': {
      title: 'Our Story',
      text: 'Tell your brand story here...',
      buttonText: 'Learn More',
      buttonLink: '/about',
    },
    'trust-badges': {
      badges: [
        { icon: 'truck', text: 'Free Shipping' },
        { icon: 'shield', text: 'Secure Payment' },
        { icon: 'refresh', text: 'Easy Returns' },
      ],
    },
    'announcement-bar': {
      text: 'Free shipping on orders over $50!',
      link: '',
      dismissible: true,
    },
    'ad-placement': {},
  };
  return defaults[type] || {};
}

// Section Icon component
function SectionIcon({ type }) {
  const icons = {
    layout: <path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />,
    images: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>,
    play: <><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></>,
    grid: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
    folder: <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />,
    quote: <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21zM15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />,
    mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></>,
    columns: <><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    megaphone: <path d="M3 11l18-5v12L3 13v-2zM11.6 16.8a3 3 0 11-5.8-1.6" />,
    ad: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 17V7l7 10V7" /></>,
  };

  return (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      {icons[type] || icons.layout}
    </svg>
  );
}

// Shop Settings Modal
function ShopSettingsModal({ settings, onUpdate, onClose }) {
  const [layoutType, setLayoutType] = useState(settings.layoutType || 'grid-classic');
  const [productsPerRow, setProductsPerRow] = useState(settings.productsPerRow || 4);
  const [enableProductOverlay, setEnableProductOverlay] = useState(settings.enableProductOverlay || false);
  const [heroProductCount, setHeroProductCount] = useState(settings.heroProductCount || 1);
  const [cardStyle, setCardStyle] = useState(settings.cardStyle || 'standard');

  const handleSave = () => {
    onUpdate({
      layoutType,
      productsPerRow,
      enableProductOverlay,
      heroProductCount,
      cardStyle,
    });
    onClose();
  };

  return (
    <div className="tb-modal-overlay" onClick={onClose}>
      <div className="tb-modal tb-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="tb-modal__header">
          <h3>Shop Page Settings</h3>
          <button className="tb-modal__close" onClick={onClose}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="tb-modal__body">
          {/* Layout Type */}
          <div className="tb-settings-group">
            <h4>Shop Layout</h4>
            <p className="tb-settings-group__hint">Choose how products are displayed on your shop page</p>
            <div className="tb-layout-options">
              <label className={`tb-layout-option ${layoutType === 'grid-classic' ? 'tb-layout-option--active' : ''}`}>
                <input
                  type="radio"
                  name="layoutType"
                  value="grid-classic"
                  checked={layoutType === 'grid-classic'}
                  onChange={(e) => setLayoutType(e.target.value)}
                />
                <div className="tb-layout-option__preview tb-layout-option__preview--grid">
                  <div></div><div></div><div></div><div></div>
                  <div></div><div></div><div></div><div></div>
                </div>
                <span className="tb-layout-option__name">Classic Grid</span>
                <span className="tb-layout-option__desc">Standard uniform product grid</span>
              </label>

              <label className={`tb-layout-option ${layoutType === 'grid-masonry' ? 'tb-layout-option--active' : ''}`}>
                <input
                  type="radio"
                  name="layoutType"
                  value="grid-masonry"
                  checked={layoutType === 'grid-masonry'}
                  onChange={(e) => setLayoutType(e.target.value)}
                />
                <div className="tb-layout-option__preview tb-layout-option__preview--masonry">
                  <div style={{height: '60px'}}></div>
                  <div style={{height: '40px'}}></div>
                  <div style={{height: '50px'}}></div>
                  <div style={{height: '35px'}}></div>
                  <div style={{height: '45px'}}></div>
                  <div style={{height: '55px'}}></div>
                </div>
                <span className="tb-layout-option__name">Masonry</span>
                <span className="tb-layout-option__desc">Pinterest-style staggered layout</span>
              </label>

              <label className={`tb-layout-option ${layoutType === 'magazine' ? 'tb-layout-option--active' : ''}`}>
                <input
                  type="radio"
                  name="layoutType"
                  value="magazine"
                  checked={layoutType === 'magazine'}
                  onChange={(e) => setLayoutType(e.target.value)}
                />
                <div className="tb-layout-option__preview tb-layout-option__preview--magazine">
                  <div className="tb-layout-option__hero"></div>
                  <div className="tb-layout-option__grid">
                    <div></div><div></div><div></div>
                  </div>
                </div>
                <span className="tb-layout-option__name">Magazine</span>
                <span className="tb-layout-option__desc">Featured hero + smaller grid</span>
              </label>
            </div>
          </div>

          {/* Products Per Row */}
          <div className="tb-settings-group">
            <h4>Products Per Row</h4>
            <div className="tb-slider-row">
              <input
                type="range"
                min="3"
                max="4"
                value={productsPerRow}
                onChange={(e) => setProductsPerRow(parseInt(e.target.value))}
                className="tb-slider"
              />
              <span className="tb-slider-value">{productsPerRow}</span>
            </div>
          </div>

          {/* Magazine Hero Count */}
          {layoutType === 'magazine' && (
            <div className="tb-settings-group">
              <h4>Hero Products</h4>
              <p className="tb-settings-group__hint">Number of large featured products at the top</p>
              <div className="tb-slider-row">
                <input
                  type="range"
                  min="1"
                  max="2"
                  value={heroProductCount}
                  onChange={(e) => setHeroProductCount(parseInt(e.target.value))}
                  className="tb-slider"
                />
                <span className="tb-slider-value">{heroProductCount}</span>
              </div>
            </div>
          )}

          {/* Product Overlay */}
          <div className="tb-settings-group">
            <h4>Product Quick View</h4>
            <label className="tb-toggle-row">
              <span>Enable Product Overlay</span>
              <div className={`tb-toggle ${enableProductOverlay ? 'tb-toggle--active' : ''}`}>
                <input
                  type="checkbox"
                  checked={enableProductOverlay}
                  onChange={(e) => setEnableProductOverlay(e.target.checked)}
                />
                <span className="tb-toggle__slider"></span>
              </div>
            </label>
            <p className="tb-settings-group__hint">
              When enabled, clicking a product opens a sleek overlay instead of navigating to a new page
            </p>
          </div>

          {/* Card Style */}
          <div className="tb-settings-group">
            <h4>Card Style</h4>
            <select
              value={cardStyle}
              onChange={(e) => setCardStyle(e.target.value)}
              className="tb-select"
            >
              <option value="standard">Standard - Image, name, price</option>
              <option value="minimal">Minimal - Image and price only</option>
              <option value="detailed">Detailed - With description preview</option>
            </select>
          </div>
        </div>

        <div className="tb-modal__footer">
          <button className="tb-modal__btn tb-modal__btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="tb-modal__btn tb-modal__btn--primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Global Settings Modal
function GlobalSettingsModal({ styles, onUpdate, onClose, onOpenStorefrontSettings }) {
  const [colors, setColors] = useState(styles.colors || {});
  const [darkColors, setDarkColors] = useState(styles.darkColors || {});
  const [fonts, setFonts] = useState(styles.fonts || {});
  const [paletteMode, setPaletteMode] = useState('light');

  const handleSave = () => {
    onUpdate({ colors, darkColors, fonts });
    onClose();
  };

  return (
    <div className="tb-modal-overlay" onClick={onClose}>
      <div className="tb-modal" onClick={e => e.stopPropagation()}>
        <div className="tb-modal__header">
          <h3>Global Settings</h3>
          <button className="tb-modal__close" onClick={onClose}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="tb-modal__body">
          <div className="tb-appearance-shortcut">
            <div>
              <h4>Storefront appearance</h4>
              <p>Set the default Light, Dark, or Match device behaviour and choose whether customers can switch appearance.</p>
            </div>
            <button
              type="button"
              className="tb-appearance-shortcut__button"
              onClick={() => {
                onClose();
                onOpenStorefrontSettings();
              }}
            >
              Manage appearance
            </button>
          </div>

          <div className="tb-settings-group">
            <div className="tb-palette-heading">
              <div><h4>Colour system</h4><p>Used by every storefront route and section.</p></div>
              <div className="tb-palette-mode" role="tablist" aria-label="Palette mode">
                <button type="button" className={paletteMode === 'light' ? 'is-active' : ''} onClick={() => setPaletteMode('light')}>Light</button>
                <button type="button" className={paletteMode === 'dark' ? 'is-active' : ''} onClick={() => setPaletteMode('dark')}>Dark</button>
              </div>
            </div>
            <div className="tb-color-grid">
              {['primary', 'secondary', 'accent', 'background', 'text', 'muted'].map(key => (
                <div key={key} className="tb-color-input">
                  <label>{key.charAt(0).toUpperCase() + key.slice(1)}</label>
                  <div className="tb-color-input__wrapper">
                    <input
                      type="color"
                      value={(paletteMode === 'dark' ? darkColors : colors)[key] || '#000000'}
                      onChange={e => (paletteMode === 'dark' ? setDarkColors : setColors)(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                    <input
                      type="text"
                      value={(paletteMode === 'dark' ? darkColors : colors)[key] || ''}
                      onChange={e => (paletteMode === 'dark' ? setDarkColors : setColors)(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tb-settings-group">
            <h4>Fonts</h4>
            <div className="tb-font-inputs">
              <div className="tb-font-input">
                <label>Heading Font</label>
                <select
                  value={fonts.heading || 'Inter'}
                  onChange={e => setFonts(prev => ({ ...prev, heading: e.target.value }))}
                >
                  <option value="Inter, sans-serif">Inter</option>
                  <option value="Playfair Display, serif">Playfair Display</option>
                  <option value="Poppins, sans-serif">Poppins</option>
                  <option value="Montserrat, sans-serif">Montserrat</option>
                  <option value="Space Grotesk, sans-serif">Space Grotesk</option>
                  <option value="Cormorant, serif">Cormorant</option>
                  <option value="Nunito, sans-serif">Nunito</option>
                </select>
              </div>
              <div className="tb-font-input">
                <label>Body Font</label>
                <select
                  value={fonts.body || 'Inter'}
                  onChange={e => setFonts(prev => ({ ...prev, body: e.target.value }))}
                >
                  <option value="Inter, sans-serif">Inter</option>
                  <option value="Lato, sans-serif">Lato</option>
                  <option value="Open Sans, sans-serif">Open Sans</option>
                  <option value="Roboto, sans-serif">Roboto</option>
                  <option value="Quicksand, sans-serif">Quicksand</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="tb-modal__footer">
          <button className="tb-modal__btn tb-modal__btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="tb-modal__btn tb-modal__btn--primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default ThemeBuilder;
